/**
 * Per-dimension look: called every frame right after Sky.update(), it either
 * leaves the overworld sky alone or turns the shared environment uniforms into
 * the Nether's (or the End's) — no sun, moon, stars or clouds, no direct light, a flat
 * red-brown fog (vanilla nether_wastes fog 0x330808) close around the player
 * and a constant ambient floor (vanilla ambient_light 0.1, which reads roughly
 * like light level 8 everywhere). Colours stay neutral (no extra grading).
 */

import * as THREE from 'three';
import type { EnvUniforms } from './env';
import { dimensionSettings, type DimensionId } from '../world/dimension';
import type { NetherBiome } from '../worldgen/nether';

/** Vanilla per-biome fog colours (sRGB). */
const BIOME_FOG: Record<number, number> = {
  0: 0x330808, // nether wastes
  1: 0x1b4745, // soul sand valley
  2: 0x330303, // crimson forest
  3: 0x1a051a, // warped forest
  4: 0x685f70, // basalt deltas
};

const fog = new THREE.Color();
const target = new THREE.Color();
/** Linear ambient radiance in the nether (warm, dim; ~level 8 brightness). */
const NETHER_AMBIENT = new THREE.Color(0.17, 0.14, 0.13);
/** The End: vanilla forces a bright lightmap there, so a brighter, slightly cool floor under a near-black purple fog. */
const END_AMBIENT = new THREE.Color(0.36, 0.35, 0.4);

export class DimensionEnv {
  private hiddenFor: DimensionId | null = null;

  constructor(
    private readonly env: EnvUniforms,
    private readonly skyGroup: THREE.Object3D,
    private readonly scene: THREE.Scene,
  ) {}

  /**
   * Apply after Sky.update(). `fogFar` is the render-distance fog end in blocks;
   * `biome` (nether only) picks the fog tint, eased over time.
   */
  apply(dim: DimensionId, fogFar: number, underwater: boolean, dt: number, biome?: NetherBiome): void {
    const env = this.env;
    const set = dimensionSettings(dim);
    if (set.hasSky) {
      env.uDimAmbient.value.setRGB(0, 0, 0);
      if (this.hiddenFor !== null) {
        this.skyGroup.visible = true;
        this.scene.background = null;
        this.hiddenFor = null;
      }
      return;
    }
    if (this.hiddenFor !== dim) {
      this.skyGroup.visible = false;
      this.hiddenFor = dim;
      fog.setHex(set.fogColor);
    }
    target.setHex(biome !== undefined ? BIOME_FOG[biome] ?? set.fogColor : set.fogColor);
    fog.lerp(target, Math.min(1, dt * 0.8));
    if (!(this.scene.background instanceof THREE.Color)) this.scene.background = new THREE.Color();
    (this.scene.background as THREE.Color).copy(fog);

    env.uSunColor.value.setRGB(0, 0, 0);
    env.uSunGlow.value.setRGB(0, 0, 0);
    env.uAmbientColor.value.setRGB(0, 0, 0); // no skylight anyway
    env.uDimAmbient.value.copy(dim === 'end' ? END_AMBIENT : NETHER_AMBIENT);
    env.uZenith.value.copy(fog);
    env.uHorizon.value.copy(fog);
    if (!underwater) {
      env.uFogColor.value.copy(fog);
      // vanilla isFoggyAt: fog from 5% to 50% of the view distance (capped at 96)
      const far = Math.min(fogFar, 192);
      env.uFogNear.value = far * set.fogNear;
      env.uFogFar.value = far * set.fogFar;
    }
  }
}
